<template>
  <div class="editable-table-container">
    <div
      v-if="tooltipVisible"
      class="custom-tooltip"
      :class="tooltipPosition"
      :style="tooltipStyle"
    >
      {{ tooltipText }}
    </div>
    <table class="editable-table">
      <TableHeader>
        <tr>
          <th class="row-number-header">
            #
          </th>
          <th v-for="(header, index) in headers" :key="index" :style="{width: header.width || 'auto'}">
            {{ header.text }}
          </th>
        </tr>
      </TableHeader>
      <tbody>
        <tr v-for="(row, rowIndex) in tableData" :key="rowIndex">
          <td class="row-number-cell">
            {{ rowIndex + 1 }}
          </td>
          <td
            v-for="(cell, cellIndex) in row"
            :key="cellIndex"
            :class="{'error-cell': hasError(rowIndex, cellIndex)}"
            @mouseenter="showTooltip(rowIndex, cellIndex, $event)"
            @mouseleave="hideTooltip"
          >
            <div v-if="editMode === `${rowIndex}-${cellIndex}`" class="editable-cell-container">
              <CustomInput
                ref="editInput"
                :model-value="cell"
                @update:modelValue="updateCell(rowIndex, cellIndex, $event)"
                @keyup:enter="saveCell"
                @keyup:esc="finishEditing"
              />
            </div>
            <div v-else class="cell-content" @click="startEditing(rowIndex, cellIndex)">
              {{ cell }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import TableHeader from './TableHeader.vue';
import CustomInput from '../inputs/CustomInput.vue';

export default {
  name: 'EditableTable',
  components: {
    TableHeader,
    CustomInput
  },
  props: {
    headers: {
      type: Array,
      required: true,
      // Each header should have a text property and an optional width property
      // e.g. [{text: 'Name', width: '200px'}, {text: 'Email', width: '300px'}]
    },
    data: {
      type: Array,
      required: true,
      // Each row is an array of cell values
      // e.g. [['John Doe', 'john@example.com'], ['Jane Doe', 'jane@example.com']]
    },
    cellErrors: {
      type: Array,
      default: () => [],
      // Array of [rowIndex, cellIndex] pairs indicating cells with errors
      // e.g. [[0, 2], [1, 3]] means cell at row 0, column 2 and cell at row 1, column 3 have errors
    },
    cellErrorMessages: {
      type: Object,
      default: () => ({}),
      // Object mapping cell coordinates to error messages
      // e.g. {'0-2': 'Invalid phone number', '1-3': 'Invalid email'}
    },
  },
  data() {
    return {
      tableData: JSON.parse(JSON.stringify(this.data)),
      editMode: null, // Format: "rowIndex-cellIndex"
      tooltipVisible: false,
      tooltipText: '',
      tooltipPosition: 'tooltip-below',
      tooltipStyle: {
        top: '0px',
        left: '0px'
      },
    };
  },
  watch: {
    data: {
      handler(newValue) {
        this.tableData = JSON.parse(JSON.stringify(newValue));
      },
      deep: true
    }
  },
  methods: {
    hasError(rowIndex, cellIndex) {
      return this.cellErrors.some(error => error[0] === rowIndex && error[1] === cellIndex);
    },
    getCellErrorMessage(rowIndex, cellIndex) {
      if (!this.hasError(rowIndex, cellIndex)) return '';
      const key = `${rowIndex}-${cellIndex}`;
      return this.cellErrorMessages[key] || '';
    },
    startEditing(rowIndex, cellIndex) {
      this.saveCell();
      this.editMode = `${rowIndex}-${cellIndex}`;
      this.$nextTick(() => {
        if (this.$refs.editInput && this.$refs.editInput[0]) {
          this.$refs.editInput[0].$el.focus();
        }
      });
    },
    finishEditing() {
      this.editMode = null;
    },
    saveCell() {
      this.tableData = JSON.parse(JSON.stringify(this.tableData));
      this.$emit('update:data', this.tableData);
      this.editMode = null;
      this.$emit('cell-edited');
    },
    updateCell(rowIndex, cellIndex, value) {
      // Create a new array to ensure reactivity
      const newTableData = JSON.parse(JSON.stringify(this.tableData));

      // If this is a phone number cell (index 2), filter out non-numeric characters
      if (cellIndex === 2 && value) {
        // Allow only digits, remove all other characters
        value = value.replace(/[^\d]/g, '');
      }

      newTableData[rowIndex][cellIndex] = value;
      this.tableData = newTableData;
      this.$emit('update:data', this.tableData);
    },

    showTooltip(rowIndex, cellIndex, event) {
      if (!this.hasError(rowIndex, cellIndex)) return;

      const errorMessage = this.getCellErrorMessage(rowIndex, cellIndex);
      if (!errorMessage) return;

      this.tooltipText = errorMessage;

      // Position the tooltip below or above the cell based on available space
      const rect = event.target.getBoundingClientRect();

      // Check if we're near the bottom of the viewport
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const tooltipHeight = 40; // Approximate height of tooltip

      // If there's not enough space below, position above
      if (spaceBelow < tooltipHeight) {
        // Position above the cell
        this.tooltipStyle = {
          position: 'fixed',
          top: `${rect.top}px`,
          left: `${rect.left + rect.width / 2}px`,
          transform: 'translate(-50%, -100%)'
        };

        // Set the tooltip position class
        this.tooltipPosition = 'tooltip-above';
      } else {
        // Position below the cell
        this.tooltipStyle = {
          position: 'fixed',
          top: `${rect.bottom}px`,
          left: `${rect.left + rect.width / 2}px`,
          transform: 'translate(-50%, 0)'
        };

        // Set the tooltip position class
        this.tooltipPosition = 'tooltip-below';
      }

      this.tooltipVisible = true;
    },

    hideTooltip() {
      this.tooltipVisible = false;
    }
  }
};
</script>

<style scoped>
.editable-table-container {
  width: 100%;
  overflow-x: auto;
}

.editable-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.editable-table td {
  border-bottom: 1px solid #E5E7EB;
  padding: 12px 15px;
  font-size: 14px;
  color: var(--secondary-black-color);
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
  word-wrap: break-word;
}

.cell-content {
  cursor: pointer;
  min-height: 24px;
  display: flex;
  align-items: center;
}

.cell-content:hover {
  text-decoration: underline;
}

.editable-cell-container {
  width: 100%;
}

.editable-table th {
  padding: 12px 15px;
  font-size: 14px;
  text-align: left;
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
  word-wrap: break-word;
}

.error-cell {
  border: 1px solid #ff0000 !important;
  position: relative;
}

.custom-tooltip {
  position: fixed;
  padding: 8px 12px;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  pointer-events: none;
  max-width: fit-content;
  word-wrap: break-word;
  text-align: center;
  transition: opacity 0.2s ease-in-out;
  /* Ensure the tooltip is positioned correctly */
  margin: 0;
}

/* Default arrow (pointing up for tooltip below) */
.custom-tooltip::after {
  content: '';
  position: absolute;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
}

/* Arrow pointing up (for tooltip below the cell) */
.tooltip-below::after {
  bottom: 100%;
  border-color: transparent transparent #f5c6cb transparent;
}

/* Arrow pointing down (for tooltip above the cell) */
.tooltip-above::after {
  top: 100%;
  border-color: #f5c6cb transparent transparent transparent;
}

.row-number-header, .row-number-cell {
  width: 50px;
  min-width: 50px;
  max-width: 50px;
  text-align: center;
  background-color: #f9fafb;
  color: var(--secondary-black-color);
  font-weight: normal;
  user-select: none;
}

.row-number-cell {
  cursor: default;
}
</style> 
